<script lang="ts">
export default {
  name: 'DatePicker',
};
</script>

<script setup lang="ts">
import { type DateValue } from '@internationalized/date';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-vue-next';
import {
  DatePickerArrow,
  DatePickerCalendar,
  DatePickerCell,
  DatePickerCellTrigger,
  DatePickerContent,
  DatePickerField,
  DatePickerGrid,
  DatePickerGridBody,
  DatePickerGridHead,
  DatePickerGridRow,
  DatePickerHeadCell,
  DatePickerHeader,
  DatePickerHeading,
  DatePickerInput,
  DatePickerPrev,
  DatePickerNext,
  DatePickerRoot,
  DatePickerTrigger,
} from 'reka-ui';

const date = defineModel<DateValue>();

defineOptions({
  inheritAttrs: false,
});
</script>

<template>
  <DatePickerRoot v-model="date">
    <DatePickerField
      v-slot="{ segments }"
      class="tw:input tw:input-sm tw:flex tw:items-center tw:justify-between tw:bg-white tw:text-center tw:text-primary tw:select-none tw:data-[invalid]:border-red-500"
      v-bind="$attrs">
      <div class="tw:flex tw:items-center">
        <template v-for="item in segments" :key="item.part">
          <DatePickerInput v-if="item.part === 'literal'" :part="item.part" class="tw:text-gray-500">
            {{ item.value }}
          </DatePickerInput>
          <DatePickerInput
            v-else
            :part="item.part"
            class="tw:rounded-md tw:p-0.5 tw:focus:shadow-[0_0_0_1px] tw:focus:shadow-gray-500 tw:focus:outline-none tw:data-[placeholder]:text-gray-500">
            {{ item.value }}
          </DatePickerInput>
        </template>
      </div>

      <DatePickerTrigger class="tw:rounded-md tw:p-1 tw:text-xl tw:focus:shadow-[0_0_0_1px] tw:focus:shadow-gray-500">
        <CalendarIcon :size="17" />
      </DatePickerTrigger>
    </DatePickerField>
    <Teleport to="body">
      <DatePickerContent
        :side-offset="4"
        class="tw:data-[state=open]:data-[side=bottom]:animate-slide-down-and-fade tw:z-1000 tw:rounded-xl tw:bg-white tw:shadow-[0_10px_38px_-10px_hsla(206,22%,7%,.35),0_10px_20px_-15px_hsla(206,22%,7%,.2)] tw:will-change-[transform,opacity] tw:focus:shadow-[0_10px_38px_-10px_hsla(206,22%,7%,.35),0_10px_20px_-15px_hsla(206,22%,7%,.2)]">
        <DatePickerArrow class="tw:fill-white" />
        <DatePickerCalendar v-slot="{ weekDays, grid }" class="tw:p-4">
          <DatePickerHeader class="tw:flex tw:items-center tw:justify-between">
            <DatePickerPrev
              class="tw:inline-flex tw:h-8 tw:w-8 tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-[9px] tw:bg-transparent tw:text-gray-500 tw:transition-all tw:hover:bg-primary/80 tw:hover:text-white tw:focus:shadow-[0_0_0_1px] tw:focus:shadow-gray-500 tw:active:scale-98 tw:active:transition-all">
              <ChevronLeftIcon :size="17" />
            </DatePickerPrev>

            <DatePickerHeading class="tw:font-medium tw:text-black" />
            <DatePickerNext
              class="tw:inline-flex tw:h-8 tw:w-8 tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-[9px] tw:bg-transparent tw:text-gray-500 tw:transition-all tw:hover:bg-primary/80 tw:hover:text-white tw:focus:shadow-[0_0_0_1px] tw:focus:shadow-gray-500 tw:active:scale-98 tw:active:transition-all">
              <ChevronRightIcon :size="17" />
            </DatePickerNext>
          </DatePickerHeader>
          <div class="tw:flex tw:flex-col tw:space-y-4 tw:pt-4 tw:sm:flex-row tw:sm:space-y-0 tw:sm:space-x-4">
            <DatePickerGrid
              v-for="month in grid"
              :key="month.value.toString()"
              class="tw:w-full tw:border-collapse tw:space-y-1 tw:select-none">
              <DatePickerGridHead>
                <DatePickerGridRow class="tw:mb-1 tw:flex tw:w-full tw:justify-between">
                  <DatePickerHeadCell
                    v-for="day in weekDays"
                    :key="day"
                    class="tw:w-8 tw:rounded-md tw:text-xs tw:text-primary">
                    {{ day }}
                  </DatePickerHeadCell>
                </DatePickerGridRow>
              </DatePickerGridHead>
              <DatePickerGridBody>
                <DatePickerGridRow
                  v-for="(weekDates, index) in month.rows"
                  :key="`weekDate-${index}`"
                  class="tw:flex tw:w-full">
                  <DatePickerCell v-for="weekDate in weekDates" :key="weekDate.toString()" :date="weekDate">
                    <DatePickerCellTrigger
                      :day="weekDate"
                      :month="month.value"
                      class="tw:relative tw:flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-[9px] tw:border tw:border-transparent tw:bg-transparent tw:text-sm tw:font-normal tw:whitespace-nowrap tw:text-black tw:outline-none tw:before:absolute tw:before:top-[5px] tw:before:hidden tw:before:h-1 tw:before:w-1 tw:before:rounded-full tw:before:bg-white tw:hover:border-primary tw:focus:shadow-[0_0_0_1px] tw:data-[disabled]:text-black/30 tw:data-[selected]:bg-secondary tw:data-[selected]:font-medium tw:data-[selected]:text-white tw:data-[selected]:before:bg-white tw:data-[today]:before:block tw:data-[today]:before:bg-green-500 tw:data-[unavailable]:pointer-events-none tw:data-[unavailable]:text-black/30 tw:data-[unavailable]:line-through" />
                  </DatePickerCell>
                </DatePickerGridRow>
              </DatePickerGridBody>
            </DatePickerGrid>
          </div>
        </DatePickerCalendar>
      </DatePickerContent>
    </Teleport>
  </DatePickerRoot>
</template>
